import { View, Text } from "@lightningtv/solid";
import { For, Show } from "solid-js";
import theme from "theme";

const blockWidth = 900;

const ContentBlockStyle = {
  display: "flex",
  flexDirection: "column",
  flexBoundary: "fixed",
  width: blockWidth * 2,
  height: 220,
  gap: 16,
};

const HeadlineStyles = {
  ...theme.typography.display2,
  fontFamily: "Roboto",
  fontWeight: 400,
  fontSize: 72,
  lineHeight: 88,
  maxLines: 1,
  width: blockWidth * 1.5,
  contain: "width",
};
const Headline = (props) => <Text {...props} style={HeadlineStyles}></Text>;

const DescriptionStyles = {
  ...theme.typography.body1,
  fontFamily: "Roboto",
  fontWeight: 400,
  fontSize: 28,
  lineHeight: 40,
  width: 800,
  maxLines: 2,
  contain: "width",
  color: "#B9B9BE"
};

const DescriptionStylesSecond = {
  ...theme.typography.body1,
  fontFamily: "Roboto",
  fontWeight: 400,
  lineHeight: 32,
  width: blockWidth,
  maxLines: 1,
  contain: "width",
  fontSize: 22,
  color: '#b5b1b1'
};

const BadgeStyle = {
  fontSize: 16,
  lineHeight: 20,
  marginLeft: 13,
  marginRight: 13,
};

const Description = (props) => (
  <Text {...props} style={DescriptionStyles}>
    {props.children}
  </Text>
);

const DescriptionSecond = (props) => (
  <Text {...props} style={DescriptionStylesSecond}>
    {props.children}
  </Text>
);

export const Badge = (props) => {
  return (
    <View
      {...props}
      style={{
        color: "0x00000099",
        borderRadius: 8,
        border: { width: 2, color: "0xffffffff" },
        display: "flex",
        height: 36,
      }}
    >
      <Text lineHeight={36} style={BadgeStyle}>
        {props.children}
      </Text>
    </View>
  );
};

const MetaTextStyle = {
  ...theme.typography.body2,
  fontFamily: "Roboto",
  fontWeight: 400,
};

function formatDuration(duration?: string): string {
  console.log('duration', duration());

  if (typeof duration() !== 'string' || !duration().includes(':')) return '';
  const [hours, minutes] = duration().split(':');
  const h = parseInt(hours || '0', 10);
  const m = parseInt(minutes || '0', 10);
  return `${h} h ${m} m`;
}

function formatRating(rating?: number | string): string {
  const num = parseFloat(String(rating()));
  return !isNaN(num) ? num.toFixed(1) : '';
}

function formatReleaseYear(dateStr?: string): string {
  if (typeof dateStr() !== 'string' || !dateStr().includes('-')) return '';
  const year = dateStr().split('-')[0];
  return /^\d{4}$/.test(year) ? year : '';
}




const Metadata = (props) => (
  <View
    style={{
      display: "flex",
      flexDirection: "row",
      gap: 18,
      width: blockWidth,
      height: 48,
    }}
  >
    <View y={-4} src="./assets/stars.png" width={188} height={31}></View>
    <View
      y={-4}
      flexItem={false}
      clipping
      width={(188 * props.voteAverage) / 10}
      height={31}
    >
      <View src="./assets/stars-full.png" width={188} height={31}></View>
    </View>
    {/* @ts-ignore */}
    <Text style={MetaTextStyle}>{props.voteCount} reviews</Text>
    {/* @ts-ignore */}
    <Text style={MetaTextStyle}>{props.metaText}</Text>
    <For each={props.badges}>{(item) => <Badge y={-5}>{item}</Badge>}</For>
  </View>
);

const ContentBlock = (props) => {
  const unwrap = (value) => (typeof value === 'function' ? value() : value);

  const hasText = (value) => {
    const raw = unwrap(value);
    return raw !== undefined && raw !== null && String(raw).trim() !== '';
  };

  const formatRating = (rating) => {
    const raw = unwrap(rating);
    return raw !== undefined && raw !== null && !isNaN(Number(raw)) && Number(raw) > 0
      ? `${parseFloat(raw.toString()).toFixed(1)}/10`
      : '';
  };

  const formatReleaseYear = (release) => {
    const raw = unwrap(release);
    if (!raw) return '';
    const year = new Date(raw).getFullYear();
    return isNaN(year) ? '' : String(year);
  };

  const formatMinutes = (duration) => {
    const raw = unwrap(duration);
    if (!hasText(raw)) return '';
    const minutes = Number(raw);
    if (isNaN(minutes) || minutes <= 0) return String(raw);
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  // Jetstream-style details line: rating • year • genre • duration
  const detailsLine = () =>
    [
      formatRating(props.content.rating),
      formatReleaseYear(props.content.release),
      hasText(props.content.genre) ? String(unwrap(props.content.genre)) : '',
      formatMinutes(props.content.duration),
    ]
      .filter(Boolean)
      .join('  •  ');

  // Jetstream "details landing": title, 2-line plot summary, details line,
  // crew columns (label over value). Vertical flex column so every line
  // follows the previous one — missing fields leave no gaps.
  return (
    <View
      {...props}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 24,
        width: blockWidth * 1.5,
      }}
    >
      <Headline>{props.content.title}</Headline>

      <Show when={hasText(props.content.description)}>
        <Description>{props.content.description}</Description>
      </Show>

      <Show when={detailsLine() !== ''}>
        <Text
          color={"#B9B9BE"}
          fontSize={28}
          style={{ maxLines: 1, contain: "width", width: 800, marginTop: 20 }}
        >
          {detailsLine()}
        </Text>
      </Show>

      <Show when={hasText(props.content.director) || hasText(props.content.cast)}>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 70,
            marginTop: 20,
            width: 1100,
            height: 70,
          }}
        >
          <Show when={hasText(props.content.director)}>
            <View style={{ display: "flex", flexDirection: "column", gap: 6, width: 300, height: 70 }}>
              <Text color={"#8E8E93"} fontSize={24}>Director</Text>
              <Text
                color={"#FFFFFF"}
                fontSize={26}
                style={{ maxLines: 1, contain: "width", width: 300 }}
              >
                {String(unwrap(props.content.director))}
              </Text>
            </View>
          </Show>

          <Show when={hasText(props.content.cast)}>
            <View style={{ display: "flex", flexDirection: "column", gap: 6, width: 700, height: 70 }}>
              <Text color={"#8E8E93"} fontSize={24}>Cast</Text>
              <Text
                color={"#FFFFFF"}
                fontSize={26}
                style={{ maxLines: 1, contain: "width", width: 700 }}
              >
                {String(unwrap(props.content.cast))}
              </Text>
            </View>
          </Show>
        </View>
      </Show>
    </View>
  );
};

export default ContentBlock;